import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { config } from '@/config/env';

interface Props {
  show: boolean;
  username: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface ComplexityRules {
  min_length: number;
  uppercase: number;
  lowercase: number;
  numeric: number;
  special: number;
}

export default function ChangePasswordModal({ show, username, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rules, setRules] = useState<ComplexityRules | null>(null);
  const [validationMsg, setValidationMsg] = useState('');

  // Carica regole complessità
  useEffect(() => {
    if (!show) return;
    const fetchRules = async () => {
      try {
        const url = config.WSREST_TABATA.replace(/\/+$/, '') + '/password-policy';
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          if (data?.requirements) setRules(data.requirements);
        }
      } catch { /* ignore */ }
    };
    fetchRules();
  }, [show]);

  // Reset al close
  useEffect(() => {
    if (!show) {
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
      setShowOld(false); setShowNew(false);
      setError(''); setSuccess(''); setValidationMsg('');
    }
  }, [show]);

  // Validazione complessità in tempo reale
  const validateComplexity = useCallback((pwd: string) => {
    if (!rules || !pwd) { setValidationMsg(''); return; }
    const errors: string[] = [];
    if (pwd.length < rules.min_length) errors.push(`almeno ${rules.min_length} caratteri`);
    if (rules.uppercase > 0 && !(new RegExp(`(?:.*[A-Z]){${rules.uppercase},}`)).test(pwd))
      errors.push(`almeno ${rules.uppercase} maiuscol${rules.uppercase === 1 ? 'a' : 'e'}`);
    if (rules.lowercase > 0 && !(new RegExp(`(?:.*[a-z]){${rules.lowercase},}`)).test(pwd))
      errors.push(`almeno ${rules.lowercase} minuscol${rules.lowercase === 1 ? 'a' : 'e'}`);
    if (rules.numeric > 0 && !(new RegExp(`(?:.*[0-9]){${rules.numeric},}`)).test(pwd))
      errors.push(`almeno ${rules.numeric} numer${rules.numeric === 1 ? 'o' : 'i'}`);
    if (rules.special > 0 && !(new RegExp(`(?:.*[!@#$%^&*()_+{}|:<>?~\`-]){${rules.special},}`)).test(pwd))
      errors.push(`almeno ${rules.special} caratter${rules.special === 1 ? 'e' : 'i'} special${rules.special === 1 ? 'e' : 'i'}`);
    setValidationMsg(errors.length ? 'La password deve contenere ' + errors.join(', ') : '');
  }, [rules]);

  useEffect(() => { validateComplexity(newPwd); }, [newPwd, validateComplexity]);

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!oldPwd.trim()) { setError(t('changePwd.oldRequired')); return; }
    if (!newPwd.trim()) { setError(t('changePwd.newRequired')); return; }
    if (validationMsg) { setError(validationMsg); return; }
    if (newPwd !== confirmPwd) { setError(t('changePwd.mismatch')); return; }

    setLoading(true);
    try {
      const url = config.WSREST_TABATA.replace(/\/+$/, '') + '/change-password';
      const token = sessionStorage.getItem('mT');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });
      if (res.status === 401) {
        setError(t('changePwd.error'));
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        const errData = text ? JSON.parse(text) : null;
        setError(errData?.error || t('changePwd.error'));
        if (errData?.policy_description) {
          setValidationMsg(errData.policy_description);
        }
        return;
      }
      const data = await res.text().then(text => text ? JSON.parse(text) : null);
      // Aggiorna token con quelli nuovi
      if (data?.t && data?.tr && data?.tex) {
        sessionStorage.setItem('mT', data.t);
        sessionStorage.setItem('mR', data.tr);
        sessionStorage.setItem('mEx', data.tex);
      }
      setSuccess(data?.message || t('changePwd.success'));
      setTimeout(() => { onClose(); }, 2000);
    } catch {
      setError(t('changePwd.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>{t('changePwd.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="text-xs">{t('changePwd.oldPassword')}</Form.Label>
          <InputGroup>
            <Form.Control type={showOld ? 'text' : 'password'} value={oldPwd}
              onChange={e => { setOldPwd(e.target.value); setError(''); }} autoComplete="off" />
            <Button variant="outline-secondary" onClick={() => setShowOld(!showOld)} tabIndex={-1}>
              {showOld ? <FiEyeOff /> : <FiEye />}
            </Button>
          </InputGroup>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-xs">{t('changePwd.newPassword')}</Form.Label>
          <InputGroup>
            <Form.Control type={showNew ? 'text' : 'password'} value={newPwd}
              onChange={e => { setNewPwd(e.target.value); setError(''); }} autoComplete="off" />
            <Button variant="outline-secondary" onClick={() => setShowNew(!showNew)} tabIndex={-1}>
              {showNew ? <FiEyeOff /> : <FiEye />}
            </Button>
          </InputGroup>
          {validationMsg && <Form.Text className="text-danger" style={{ fontSize: 11 }}>{validationMsg}</Form.Text>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-xs">{t('changePwd.confirmPassword')}</Form.Label>
          <Form.Control type="password" value={confirmPwd}
            onChange={e => { setConfirmPwd(e.target.value); setError(''); }} autoComplete="off" />
          {confirmPwd && newPwd !== confirmPwd && (
            <Form.Text className="text-danger" style={{ fontSize: 11 }}>{t('changePwd.mismatch')}</Form.Text>
          )}
        </Form.Group>

        {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
        {success && <Alert variant="success" className="py-2" style={{ fontSize: 13 }}>{success}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>{t('actions.cancel')}</Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} disabled={loading || !!success}>
          {loading && <span className="spinner-border spinner-border-sm me-2" />}
          {t('actions.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// No parseRules needed - /password-policy returns requirements directly
